import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getUserBookings } from '../../booking/store/bookingSlice';
import QuickServiceGrid from '../components/QuickServiceGrid';
import Button from '../../shared/components/Button';
import PredictiveSearch from '../../shared/components/PredictiveSearch';
import FxGlyph from '../components/FxGlyph';
import { URGENCY } from '../../../utils/constants';
import { unanimousCallOutAmount } from '../../booking/utils/callOutFeeDisplay';
import { useBookingDraft } from '../../booking/bookingDraftContext';
import SEOHead from '../../../components/SEO/SEOHead';
import { publicCanonical } from '../../../components/SEO/siteUrl';
import { getHomePageSchema } from '../../../components/SEO/structuredData';
import HeroStoreBadges from '../components/HeroStoreBadges';
import useAfterIdle from '../../../utils/useAfterIdle';

const BookAgainCard = lazy(() => import('../components/BookAgainCard'));
const ActionableBookingBanner = lazy(() => import('../../booking/components/ActionableBookingBanner'));
const PWAInstallButton = lazy(() => import('../../../components/PWAInstallButton'));
const HomeStory = lazy(() => import('../components/HomeLowerSections').then((mod) => ({ default: mod.HomeStory })));
const HomeClosing = lazy(() => import('../components/HomeLowerSections').then((mod) => ({ default: mod.HomeClosing })));

function IdleChunk({ children }) {
  const ready = useAfterIdle();
  if (!ready) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
}



/**
 * HomePage Component
 * Modern, minimal-click interface for quick service booking
 */
const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { bookings, loading: bookingsLoading } = useSelector((state) => state.booking);
  const { popularServices } = useSelector((state) => state.service);
  // Homepage strip is not a chosen service. Show a ₹ digit only when every
  // already-loaded popular service shares that callOutFee field.
  const emergencyFee = unanimousCallOutAmount(popularServices, URGENCY.EMERGENCY);
  const sameDayFee = unanimousCallOutAmount(popularServices, URGENCY.HOURS_24);
  const within48Fee = unanimousCallOutAmount(popularServices, URGENCY.HOURS_48);
  const { setBookingDraft } = useBookingDraft();
  // The Same day tile is selected on first paint. Book a Service can carry that
  // choice. Service icons do not: they only name the service. Fee only when the strip has one.
  const sameDayIntent = {
    urgency: URGENCY.HOURS_24,
    ...(sameDayFee != null ? { callOutFee: sameDayFee } : {}),
  };
  const [showStickySearch, setShowStickySearch] = useState(false);
  const heroRef = useRef(null);
  const [selectedAddress, setSelectedAddress] = useState(() => {
    // Load from localStorage on initial mount
    try {
      const saved = localStorage.getItem('selectedAddress');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const bookingsReady = useAfterIdle();

  // Redirect technicians to their dashboard
  useEffect(() => {
    if (isAuthenticated && user?.role === 'technician') {
      navigate('/technician/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Transform bookings data for recent services and fetch ratings
  useEffect(() => {
    if (isAuthenticated && user && bookings && bookings.length > 0) {
      // Sort bookings by creation date (most recent first)
      const sortedBookings = [...bookings].sort((a, b) => {
        // Sort by createdAt date, most recent first
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        return bDate - aDate;
      });

      const transformedBookings = sortedBookings.slice(0, 6).map(booking => ({
        id: booking._id,
        serviceId: booking.serviceId?.slug || booking.serviceId?._id,
        serviceName: booking.serviceId?.name || 'Service',
        serviceIcon: booking.serviceId?.icon || '🔧',
        completedAt: booking.completedAt || booking.createdAt,
        updatedAt: booking.updatedAt || booking.createdAt,
        technician: {
          name: booking.assignedTechnicianId ? 
            `${booking.assignedTechnicianId.profile?.name || ''}`.trim() || 'Technician' : 
            'Processing',
          rating: booking.technicianRating || null,
        },
        urgency: booking.urgency,
        finalCost: booking.quote?.total || booking.finalCost || booking.callOutFee || 0,
        duration: booking.duration || null,
        rated: booking.customerRated || false,
        rating: null, // Will be fetched below if rated
        address: booking.address || '',
        status: booking.status,
      }));
      
      // Fetch ratings for rated bookings
      const fetchRatings = async () => {
        const bookingsWithRatings = await Promise.all(
          transformedBookings.map(async (booking) => {
            if (booking.rated) {
              try {
                const ratingService = (await import('../../rating/services/rating.service')).default;
                const rating = await ratingService.getRatingByBookingId(booking.id);
                return { ...booking, rating };
              } catch (error) {
                // If rating fetch fails, just keep rating as null
                return booking;
              }
            }
            return booking;
          })
        );
        setRecentBookings(bookingsWithRatings);
      };
      
      fetchRatings();
    } else if (bookingsLoading === false || !isAuthenticated) {
      // Only set empty if we're done loading and got no results, or if user is not authenticated
      setRecentBookings([]);
    }
  }, [bookings, bookingsLoading, user, isAuthenticated]);

  const detectLocation = useCallback(async () => {
    // Get the default address from the addresses array, or fallback to old address structure
    let defaultAddress = null;
    
    if (user?.profile?.addresses && user.profile.addresses.length > 0) {
      // Find the default address from the addresses array
      defaultAddress = user.profile.addresses.find(addr => addr.isDefault) || user.profile.addresses[0];
    } else if (user?.profile?.address) {
      // Fallback to old address structure
      defaultAddress = user.profile.address;
    }

    if (defaultAddress) {

      setSelectedAddress(defaultAddress);
      // Save to localStorage for persistence
      localStorage.setItem('selectedAddress', JSON.stringify(defaultAddress));
    }
  }, [user?.profile?.address]);

  const loadRecentBookings = useCallback(async () => {
    try {
      // Fetch recent bookings - get bookings from all statuses, prioritized by recent activity
      await dispatch(getUserBookings({ 
        page: 1, 
        limit: 10, 
        filters: {} // Get all bookings, we'll filter/prioritize in the transform
      }));
    } catch (error) {
      // Fallback to empty array if API fails
      setRecentBookings([]);
    }
  }, [dispatch]);

  const handleBookAgain = (bookingData) => {
    navigate(`/booking/new/${bookingData.serviceId}`, {
      state: { 
        prefill: bookingData,
        selectedAddress: selectedAddress
      },
    });
  };

  // Save selectedAddress to localStorage whenever it changes
  useEffect(() => {
    if (selectedAddress) {
      localStorage.setItem('selectedAddress', JSON.stringify(selectedAddress));
    }
  }, [selectedAddress]);

  // Update selectedAddress when user profile changes (e.g., after updating address in Profile page)
  useEffect(() => {
    if (isAuthenticated && user) {
      const userAddresses = user?.profile?.addresses || [];
      if (userAddresses.length > 0) {
        const defaultAddress = userAddresses.find(addr => addr.isDefault) || userAddresses[0];
        
        // Priority 1: Always check if default address changed - this is the most important check
        if (defaultAddress) {
          // Helper function to compare addresses (handles both _id as string and ObjectId)
          const addressesMatch = (addr1, addr2) => {
            if (!addr1 || !addr2) return false;
            const id1 = String(addr1._id || '');
            const id2 = String(addr2._id || '');
            return id1 === id2 || (
              addr1.name === addr2.name && 
              addr1.zip === addr2.zip
            );
          };
          
          const isCurrentAddressDefault = selectedAddress && addressesMatch(selectedAddress, defaultAddress);
          
          if (!isCurrentAddressDefault) {
            // Default address changed or no address selected - use the new default

            setSelectedAddress(defaultAddress);
            localStorage.setItem('selectedAddress', JSON.stringify(defaultAddress));
            return; // Exit early since we've set the default
          }
        }
        
        // Priority 2: Check if the current selectedAddress still exists and is valid
        if (selectedAddress) {
          // Helper function to check if address exists in array
          const addressExists = (addr, addressArray) => {
            return addressArray.some(a => {
              const id1 = String(addr._id || '');
              const id2 = String(a._id || '');
              return id1 === id2 || (addr.name === a.name && addr.zip === a.zip);
            });
          };
          
          if (!addressExists(selectedAddress, userAddresses)) {
            // Current selectedAddress no longer exists, update to default
            if (defaultAddress) {

              setSelectedAddress(defaultAddress);
              localStorage.setItem('selectedAddress', JSON.stringify(defaultAddress));
            }
          } else {
            // Update selectedAddress with latest data from profile (in case address was edited)
            const matchingAddress = userAddresses.find(addr => {
              const id1 = String(selectedAddress._id || '');
              const id2 = String(addr._id || '');
              return id1 === id2 || (selectedAddress.name === addr.name && selectedAddress.zip === addr.zip);
            });
            if (matchingAddress && JSON.stringify(matchingAddress) !== JSON.stringify(selectedAddress)) {

              setSelectedAddress(matchingAddress);
              localStorage.setItem('selectedAddress', JSON.stringify(matchingAddress));
            }
          }
        } else if (defaultAddress) {
          // No selectedAddress, set default

          setSelectedAddress(defaultAddress);
          localStorage.setItem('selectedAddress', JSON.stringify(defaultAddress));
        }
      }
    }
  }, [user?.profile?.addresses, isAuthenticated, user]);

  const handleRate = (bookingId) => {
    navigate(`/bookings/${bookingId}/rate`);
  };

  // Clear selectedAddress when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedAddress(null);
      localStorage.removeItem('selectedAddress');
    }
  }, [isAuthenticated]);

  // Initialize component on mount
  useEffect(() => {
    if (isAuthenticated && user) {
      const userAddresses = user?.profile?.addresses || [];
      
      // Helper function to compare addresses (handles both _id as string and ObjectId)
      const addressesMatch = (addr1, addr2) => {
        if (!addr1 || !addr2) return false;
        const id1 = String(addr1._id || '');
        const id2 = String(addr2._id || '');
        return id1 === id2 || (
          addr1.name === addr2.name && 
          addr1.zip === addr2.zip
        );
      };
      
      // Priority 1: Always use the default address if available
      if (userAddresses.length > 0) {
        const defaultAddress = userAddresses.find(addr => addr.isDefault) || userAddresses[0];
        
        if (defaultAddress) {
          // Check if localStorage has a different address saved
          const savedAddress = (() => {
            try {
              const saved = localStorage.getItem('selectedAddress');
              return saved ? JSON.parse(saved) : null;
            } catch {
              return null;
            }
          })();
          
          // Only use saved address if it's the same as the default
        if (savedAddress && addressesMatch(savedAddress, defaultAddress)) {
            // Saved address matches default, use it (with latest data from profile)

            setSelectedAddress(defaultAddress);
          } else {
            // Default address is different from saved, use default

            setSelectedAddress(defaultAddress);
            localStorage.setItem('selectedAddress', JSON.stringify(defaultAddress));
          }
        } else {
          // No default address, try to use saved address if it exists
          const savedAddress = (() => {
            try {
              const saved = localStorage.getItem('selectedAddress');
              return saved ? JSON.parse(saved) : null;
            } catch {
              return null;
            }
          })();
          
          if (savedAddress) {
            // Helper function to check if address exists in array
            const addressExists = (addr, addressArray) => {
              return addressArray.some(a => {
                const id1 = String(addr._id || '');
                const id2 = String(a._id || '');
                return id1 === id2 || (addr.name === a.name && addr.zip === a.zip);
              });
            };
            
            if (addressExists(savedAddress, userAddresses)) {
              const matchingAddress = userAddresses.find(addr => {
                const id1 = String(savedAddress._id || '');
                const id2 = String(addr._id || '');
                return id1 === id2 || (savedAddress.name === addr.name && savedAddress.zip === addr.zip);
              });

              setSelectedAddress(matchingAddress);
            } else {
              // Saved address no longer exists, use first address

              localStorage.removeItem('selectedAddress');
              if (userAddresses[0]) {
                setSelectedAddress(userAddresses[0]);
                localStorage.setItem('selectedAddress', JSON.stringify(userAddresses[0]));
              } else {
                detectLocation();
              }
            }
          } else {
            // No saved address, use first address or detect location
            if (userAddresses[0]) {

              setSelectedAddress(userAddresses[0]);
              localStorage.setItem('selectedAddress', JSON.stringify(userAddresses[0]));
            } else {
              detectLocation();
            }
          }
        }
      } else {
        // No addresses in profile, try to detect location
        detectLocation();
      }
      
      if (bookingsReady) {
        loadRecentBookings();
      }
    } else {
      // Not authenticated, try to detect location
      detectLocation();
    }
  }, [isAuthenticated, user, bookingsReady, loadRecentBookings, detectLocation]);

  useEffect(() => {
    setBookingDraft({
      urgency: URGENCY.HOURS_24,
      ...(sameDayFee != null ? { callOutFee: sameDayFee } : {}),
    });
    return () => setBookingDraft(null);
  }, [setBookingDraft, sameDayFee]);

  // Show sticky search bar once the hero scrolls out of view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickySearch(!entry.isIntersecting),
      { threshold: 0 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  const startBooking = (path, bookingState) => {
    navigate(path, bookingState ? { state: bookingState } : undefined);
  };

  const startEmergency = () => {
    const emergencyPath = '/booking/new/emergency';
    const emergencyState = {
      isEmergency: true,
      urgency: 'emergency',
      ...(emergencyFee != null ? { callOutFee: emergencyFee } : {}),
    };
    if (!isAuthenticated || !user) {
      navigate('/login', {
        state: {
          from: { pathname: emergencyPath, state: emergencyState },
        },
      });
    } else {
      navigate(emergencyPath, { state: emergencyState });
    }
  };

  return (
    <>
      <SEOHead
        title="Home Maintenance in Kochi, Trivandrum & Calicut | Fixome"
        description="Book trusted technicians for plumbing, electrical, AC, painting, pest control, and deep cleaning in Kerala. Fast response, transparent pricing. Mon-Sun 8am-7pm."
        keywords="home repair kerala, plumbing service kochi, electrician kerala, AC repair, home maintenance, technician booking, emergency repair, pest control kerala, painting services, deep cleaning"
        structuredData={getHomePageSchema()}
        url={publicCanonical('/')}
        canonical={publicCanonical('/')}
        image="/logo_transparent.png"
      />

      {/* Sticky Search Bar — appears when hero scrolls out of view */}
      {showStickySearch && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-4 py-3 flex items-center justify-center shadow-soft animate-slide-down">
          <div className="w-full max-w-2xl">
            <PredictiveSearch
              placeholder="What needs fixing? (e.g., water leak, broken AC, no power)"
              className="text-secondary-800 placeholder-gray-400 focus:outline-none"
              showPopularTerms={false}
              selectedAddress={selectedAddress}
              compact
              variant="white"
            />
          </div>
        </div>
      )}
      
      <div className="min-h-screen">
        <section ref={heroRef} className="fx-hero-wrap relative z-10">
          <div className="fx-hero-card">
            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <h1 className="fx-hero-title">
                Premium home maintenance,{' '}
                <span className="fx-hero-accent">on demand.</span>
              </h1>
              {user && (
                <p className="text-base md:text-sm mt-3 text-white">
                  Welcome back, {user.profile?.firstName || user.profile?.name?.split(' ')[0] || 'there'}.
                </p>
              )}
              <p className="fx-hero-support">
                Book trusted professionals in Kochi, Thiruvananthapuram &amp; Calicut — emergency, same day, or within 48 hours.
              </p>

              <div className="relative max-w-3xl mx-auto mt-5 md:mt-6 fx-search-on-hero">
              <PredictiveSearch
              placeholder="What needs fixing?"
              className="text-base focus:outline-none"
              showPopularTerms={true}
              selectedAddress={selectedAddress}
              variant="hero"
            />
                <button
                  type="button"
                  onClick={() => startBooking('/booking/new', sameDayIntent)}
                  className="fx-btn-primary fx-btn-block mt-3"
                >
                  Book a Service
                  <span aria-hidden="true"> →</span>
                </button>
              </div>
              <HeroStoreBadges />
            </div>
          </div>
          <div id="fx-popular-slot" className="fx-popular-slot" />
        </section>

        {/* Main Content */}
        <div className="fx-home-main max-w-7xl mx-auto px-4 py-4 md:py-6">
          {/* Actionable booking / pay-now banner (authenticated customers) */}
          <div className="fx-home-auth-slot">
            {isAuthenticated && user?.role !== 'technician' && (
              <Suspense fallback={null}>
                <ActionableBookingBanner bookings={bookings} />
              </Suspense>
            )}
          </div>

          <section className="fx-panel fx-emergency-strip mt-4" aria-labelledby="emergency-strip-heading">
            <span className="fx-icon-well" aria-hidden="true"><FxGlyph name="bolt" /></span>
            <div className="min-w-0 flex-1">
              <h2 id="emergency-strip-heading">Emergency</h2>
              <p>Urgent failure. Emergency booking fee applies.</p>
            </div>
            <button type="button" onClick={startEmergency} className="fx-btn-primary">
              Get Help Now
              <span aria-hidden="true"> →</span>
            </button>
          </section>

          {/* Expert services — existing catalogue only, circular red wells */}
          <div className="fx-panel mt-4 px-3 py-4 md:px-6 md:py-5">
            <h2 className="text-lg md:text-xl font-extrabold mb-3" style={{ color: 'var(--fx-ink, #191c1d)' }}>
              Expert Services
            </h2>
            <QuickServiceGrid selectedAddress={selectedAddress} />
            <p className="text-center mt-3 mb-1">
              <Link
                to="/services"
                className="inline-flex items-center min-h-12 md:min-h-0 text-base md:text-sm font-semibold hover:underline"
                style={{ color: 'var(--fx-primary, #e10600)' }}
              >
                See all services
              </Link>
            </p>
          </div>

        {/* CTA for users who don't find their service */}
        <div className="text-center mt-4 mb-2">
          <p className="text-base md:text-sm" style={{ color: 'var(--fx-muted, #555f6f)' }}>
            Don't see your service?{' '}
            <button
              onClick={() => navigate('/contact')}
              className="font-semibold hover:underline"
              style={{ color: 'var(--fx-primary, #e10600)' }}
            >
              Tell us what you need →
            </button>
          </p>
        </div>

        {/* Urgency strip — values stay emergency | 24h | 48h */}
        <section className="mt-6 md:mt-10 mb-6" aria-labelledby="urgency-strip-heading">
          <div className="text-center mb-4">
            <h2 id="urgency-strip-heading" className="text-xl md:text-2xl font-extrabold" style={{ color: 'var(--fx-ink, #191c1d)' }}>
              Choose how soon you need us.
            </h2>
          </div>
          <div className="fx-choice-grid">
            <button
              type="button"
              onClick={() => startBooking('/booking/new/emergency', {
                isEmergency: true,
                urgency: 'emergency',
                ...(emergencyFee != null ? { callOutFee: emergencyFee } : {}),
              })}
              className="fx-choice-card"
            >
              <span className="fx-choice-icon" aria-hidden="true"><FxGlyph name="bolt" /></span>
              <span>
                <span className="fx-choice-title">Emergency</span>
                {emergencyFee != null && <span className="fx-choice-fee">₹{emergencyFee}</span>}
                <span className="fx-choice-sub">Urgent failure. Emergency booking fee applies.</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => startBooking('/booking/new', {
                urgency: '24h',
                ...(sameDayFee != null ? { callOutFee: sameDayFee } : {}),
              })}
              className="fx-choice-card is-selected"
            >
              <span className="fx-choice-icon" aria-hidden="true"><FxGlyph name="clock" /></span>
              <span>
                <span className="fx-choice-title">Same day</span>
                {sameDayFee != null && <span className="fx-choice-fee">₹{sameDayFee}</span>}
                <span className="fx-choice-sub">A visit within 24 hours.</span>
              </span>
              <span className="fx-choice-check" aria-hidden="true">✓</span>
            </button>
            <button
              type="button"
              onClick={() => startBooking('/booking/new', {
                urgency: '48h',
                ...(within48Fee != null ? { callOutFee: within48Fee } : {}),
              })}
              className="fx-choice-card"
            >
              <span className="fx-choice-icon" aria-hidden="true"><FxGlyph name="calendar" /></span>
              <span>
                <span className="fx-choice-title">Within 48 hours</span>
                {within48Fee != null && <span className="fx-choice-fee">₹{within48Fee}</span>}
                <span className="fx-choice-sub">Planned, but soon.</span>
              </span>
            </button>
          </div>
        </section>

        <section className="fx-trust my-6" aria-label="Trust figures">
          <div>
            <strong>5,000+</strong>
            <span>Services</span>
          </div>
          <div>
            <strong>4.9</strong>
            <span>Average Rating</span>
          </div>
          <div>
            <strong>500+</strong>
            <span>Verified Pros</span>
          </div>
        </section>

        {/* Recent Bookings — only shown to authenticated users */}
        <div className="fx-home-recent-slot">
        {isAuthenticated && (
          <section className="mt-2 sm:mt-8 min-h-[200px]">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                Recent Services
              </h2>
              <button
                onClick={() => navigate('/bookings')}
                className="text-sm font-semibold text-primary-500 hover:text-primary-600 flex items-center gap-1 group"
              >
                View All <span className="group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </div>

            {bookingsLoading ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded"></div>
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                      </div>
                      <div className="flex justify-between">
                        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-8 bg-gray-200 rounded w-20"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentBookings.length > 0 ? (
              <Suspense fallback={null}>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {recentBookings.map((booking) => (
                    <BookAgainCard
                      key={booking.id}
                      booking={booking}
                      onBookAgain={handleBookAgain}
                      onRate={handleRate}
                    />
                  ))}
                </div>
              </Suspense>
            ) : (
              // Authenticated but no bookings yet
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🔧</div>
                <h3 className="text-lg font-semibold text-secondary-800 mb-2">No Recent Services</h3>
                <p className="text-secondary-600 mb-6">
                  Book your first service to see your history here.
                </p>
                <Button
                  variant="primary"
                  onClick={() => startBooking('/booking/new', sameDayIntent)}
                >
                  Book a Service
                </Button>
              </div>
            )}
          </section>
        )}
        </div>

        <IdleChunk>
          <HomeStory />
        </IdleChunk>

        <section className="mt-16 px-4 md:px-12" aria-labelledby="areas-heading">
          <h2 id="areas-heading" className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight text-center mb-6">
            Areas we serve
          </h2>
          <ul className="flex flex-wrap justify-center gap-3 text-base md:text-sm">
            {[
              ['Kochi (Ernakulam)', '/cities/kochi'],
              ['Thiruvananthapuram', '/cities/thiruvananthapuram'],
              ['Calicut', '/cities/calicut'],
            ].map(([label, path]) => (
              <li key={path}>
                <Link
                  to={path}
                  className="inline-flex items-center min-h-12 md:min-h-0 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-900 hover:border-slate-400"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 px-4 text-center">
          <p className="text-base md:text-sm text-slate-600 mb-3">Are you a technician?</p>
          <Link
            to="/technician/apply"
            className="inline-flex items-center justify-center min-h-12 md:min-h-[44px] px-5 rounded-full border border-slate-300 text-base md:text-sm font-semibold text-slate-800"
          >
            Register as Technician
          </Link>
        </section>

        <IdleChunk>
          <HomeClosing />
        </IdleChunk>
      </div>
      
    </div>
    <IdleChunk>
      <PWAInstallButton />
    </IdleChunk>
    </>
  );
};

export default HomePage;

